import type { PaymentIntent } from "../model/paymentIntent.model";
import { isSettled } from "../model/paymentIntent.model";
import type {
  ConfirmPaymentIntentRequest,
  ConfirmSavedCardRequest,
} from "../model/confirmRequest.model";
import type { ExchangeResponse } from "../model/exchange.model";
import type { StoredPaymentMethod } from "../model/storedPaymentMethod.model";
import { request, retryOnConnectionError } from "./httpClient.service";

const EXCHANGE_PATH = "/links/exchange";

export async function exchangeCode({
  baseUrl,
  code,
}: {
  baseUrl: string;
  code: string;
}): Promise<ExchangeResponse> {
  const response = await request("POST", `${baseUrl}${EXCHANGE_PATH}`, undefined, { code });
  return (await response.json()) as ExchangeResponse;
}

function buildConfirmCardRequest(
  cardToken: string,
  saveCard: boolean,
): ConfirmPaymentIntentRequest {
  if (saveCard) {
    return {
      client_type: "BROWSER",
      payment_method: "CARD",
      payment_instrument_token: cardToken,
      transaction_type: "CIT",
      usage: "INITIAL",
    };
  }
  return {
    client_type: "BROWSER",
    payment_method: "CARD",
    payment_instrument_token: cardToken,
    saved_by_customer: false,
  };
}

// Bound client (closure-based) for the whole authenticated API surface — everything but
// exchangeCode(), which needs no token. Takes the token from exchangeCode()'s response; call
// sites never pass it around explicitly again. Two-step construction, not a client that mutates
// its own state: this function can't be called until a token already exists.
// TODO: accept an AbortSignal on each method and thread it through request() so any in-flight call can be cancelled.
export function createCheckoutCustomerApiClient({
  baseUrl,
  token,
}: {
  baseUrl: string;
  token: string;
}) {
  let lastAuthenticationId: string | undefined;

  async function getPaymentIntent(paymentIntentId: string): Promise<PaymentIntent> {
    const intent = await retryOnConnectionError(() =>
      request("GET", `${baseUrl}/payment-intents/${paymentIntentId}`, token).then(
        (response) => response.json() as Promise<PaymentIntent>,
      ),
    );
    lastAuthenticationId = intent.authentication_id;
    return intent;
  }

  async function initPayment(paymentIntentId: string): Promise<PaymentIntent> {
    const response = await request(
      "POST",
      `${baseUrl}/payment-intents/${paymentIntentId}/init`,
      token,
      {},
    );
    const intent = (await response.json()) as PaymentIntent;
    lastAuthenticationId = intent.authentication_id;

    if (isSettled(intent)) {
      return intent;
    }
    return pollUntilSettled(paymentIntentId);
  }

  async function confirmWithCard(
    paymentIntentId: string,
    cardToken: string,
    saveCard: boolean,
  ): Promise<PaymentIntent> {
    const body = buildConfirmCardRequest(cardToken, saveCard);
    await request("PATCH", `${baseUrl}/payment-intents/${paymentIntentId}`, token, body);

    return pollUntilSettled(paymentIntentId);
  }

  async function confirmWithSavedCard(
    paymentIntentId: string,
    storedPaymentMethodId: string,
  ): Promise<PaymentIntent> {
    const body: ConfirmSavedCardRequest = {
      client_type: "BROWSER",
      payment_method: "SAVED_CARD",
      stored_payment_method_id: storedPaymentMethodId,
      transaction_type: "CIT",
      usage: "SUBSEQUENT",
    };
    await request("PATCH", `${baseUrl}/payment-intents/${paymentIntentId}`, token, body);

    return pollUntilSettled(paymentIntentId);
  }

  async function pollUntilSettled(paymentIntentId: string): Promise<PaymentIntent> {
    return pollPaymentIntentUntil(
      paymentIntentId,
      isSettled,
      1000,
      120,
      "Timed out waiting for the payment intent to settle",
    );
  }

  async function pollNewAuthentication(paymentIntentId: string): Promise<PaymentIntent> {
    const previousAuthenticationId = lastAuthenticationId;
    return pollPaymentIntentUntil(
      paymentIntentId,
      (intent) => intent.authentication_id !== previousAuthenticationId,
      1000,
      10,
      "Timed out waiting for a fresh authentication_id",
    );
  }

  async function getStoredPaymentMethods(): Promise<StoredPaymentMethod[]> {
    const response = await retryOnConnectionError(() =>
      request("GET", `${baseUrl}/stored-payment-methods`, token).then(
        (response) => response.json() as Promise<StoredPaymentMethod[]>,
      ),
    );
    return response;
  }

  async function deleteStoredPaymentMethod(id: string): Promise<void> {
    await retryOnConnectionError(() =>
      request("DELETE", `${baseUrl}/stored-payment-methods/${id}`, token),
    );
  }

  async function pollPaymentIntentUntil(
    paymentIntentId: string,
    condition: (intent: PaymentIntent) => boolean,
    intervalMs: number,
    maxCount: number,
    timeoutMessage: string,
  ): Promise<PaymentIntent> {
    for (let count = 0; count < maxCount; count++) {
      const intent = await getPaymentIntent(paymentIntentId);
      if (condition(intent)) {
        return intent;
      }
      if (count < maxCount - 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    }
    throw new Error(timeoutMessage);
  }

  return {
    getPaymentIntent,
    initPayment,
    confirmWithCard,
    confirmWithSavedCard,
    pollNewAuthentication,
    getStoredPaymentMethods,
    deleteStoredPaymentMethod,
  };
}

export type CheckoutCustomerApiClient = ReturnType<typeof createCheckoutCustomerApiClient>;
